import { type DateRangeViewSetting } from '@ardoq/api-types';
import { getDateRangePickerConfigForTimeline } from '@ardoq/timeline2024';
import { type DateBetweenPickerProps } from '@ardoq/date-time-picker';
import { type Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import { toDate, type SingleRelativePickerProps } from './utils';

type ToDateBetweenPickerPropsArgs = {
  domainRange: [string | Date | null, string | Date | null];
  onDomainRangeChange: (value: DateRangeViewSetting) => void;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const toDateBetweenPickerProps = ({
  domainRange,
  onDomainRangeChange,
  locale,
  i18nLocale,
}: ToDateBetweenPickerPropsArgs): DateBetweenPickerProps => {
  const startDate = toDate(domainRange[0]);
  const endDate = toDate(domainRange[1]);
  const { startDateConfig, endDateConfig } =
    getDateRangePickerConfigForTimeline([startDate, endDate]);
  return {
    startDatePicker: {
      date: startDate,
      config: startDateConfig,
      onChange: isoString =>
        onDomainRangeChange([isoString ?? null, domainRange[1] ?? null]),
    },
    endDatePicker: {
      date: endDate,
      config: endDateConfig,
      onChange: isoString =>
        onDomainRangeChange([domainRange[0] ?? null, isoString ?? null]),
    },
    locale,
    i18nLocale,
  };
};

export type { SingleRelativePickerProps };

export type TimelineDateRangeContentProps = {
  domainRange: DateRangeViewSetting | undefined;
  onDomainRangeChange: (value: DateRangeViewSetting) => void;
  // Pre-mapped here because it requires non-trivial transforms (toDate,
  // getDateRangePickerConfigForTimeline) that don't depend on condition.
  // singleRelativePickerProps and the relative-date value are computed inside
  // the component because they require the locally-owned condition state.
  dateBetweenPickerProps: DateBetweenPickerProps;
  i18nLocale: I18nLocale;
  /*
   * Renders select dropdowns in a portal, escaping any overflow-hidden or
   * stacking-context ancestors. Use this when the component is inside a
   * clipping container such as a dropdown panel with overflow:hidden.
   * We track this explicitly so we know which call sites need it and can
   * migrate them when we replace dropdowns with popover2025.
   */
  renderInPortal?: boolean;
};

type ToTimelineDateRangeContentPropsArgs = {
  domainRange: DateRangeViewSetting | undefined;
  onDomainRangeChange: (value: DateRangeViewSetting) => void;
  locale: Locale;
  i18nLocale: I18nLocale;
  renderInPortal?: boolean;
};

export const toTimelineDateRangeContentProps = ({
  domainRange,
  onDomainRangeChange,
  locale,
  i18nLocale,
  renderInPortal,
}: ToTimelineDateRangeContentPropsArgs): TimelineDateRangeContentProps => ({
  domainRange,
  onDomainRangeChange,
  renderInPortal,
  i18nLocale,
  dateBetweenPickerProps: toDateBetweenPickerProps({
    domainRange: Array.isArray(domainRange) ? domainRange : [null, null],
    onDomainRangeChange,
    locale,
    i18nLocale,
  }),
});
